import React, { useEffect, useState } from 'react';
import { signOut, getCurrentUser, ensureProfile, getUserCount, getReferralCode, generateReferralCode } from '../services/authStore';
import { supabase } from '../services/supabaseClient';

const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;

const Navbar = ({ onHomeClick, onAdminClick, onToggleRightSidebar }) => {
  const [liveCount, setLiveCount] = useState(0);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [shareMessage, setShareMessage] = useState('');

  const refreshCount = async () => {
    const count = await getUserCount();
    setLiveCount(count);
  };

  const checkUser = async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
    if (currentUser) {
      const userProfile = await ensureProfile(currentUser);
      setProfile(userProfile);
      await loadReferralCode(currentUser.id);
    }
    setLoading(false);
  };

  const loadReferralCode = async (userId) => {
    let code = await getReferralCode(userId);
    if (!code) {
      code = await generateReferralCode(userId);
    }
    setReferralCode(code || '');
  };

  const handleShare = async () => {
    if (!referralCode) return;
    const link = `${siteUrl}/?ref=${referralCode}`;
    setShareMessage('');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Join me on DevVital', text: 'Hop in and discuss dev health with us.', url: link });
        setShareMessage('Shared!');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setShareMessage('Link copied');
      } else {
        setShareMessage(link);
      }
    } catch (err) {
      setShareMessage('Unable to share');
    }
  };

  useEffect(() => {
    refreshCount();
    
    // Handle OAuth callback
    const handleOAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (accessToken && refreshToken) {
        // Manually set the session using the tokens from the hash
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        
        if (error) {
          console.error('OAuth callback: setSession error =', error);
        }
        
        await new Promise(resolve => setTimeout(resolve, 500)); // Small additional wait
        await checkUser();
        await refreshCount();
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // Only check user on initial load if no OAuth callback
        checkUser();
      }
    };
    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    const res = await signOut();
    if (res.ok) {
      setUser(null);
      setProfile(null);
      setDropdownOpen(false);
      refreshCount();
    }
  };

  const getInitials = (name, email) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email ? email[0].toUpperCase() : '?';
  };

  if (loading) {
    return (
      <nav className="bg-black border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition" onClick={onHomeClick}>
              <div className="w-8 h-8 bg-green-btn rounded-full flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 4l12 8-12 8z" fill="#0c0c0c" />
                </svg>
              </div>
              <span className="text-xl md:text-2xl font-bold tracking-tighter">DevVital</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Live</p>
                <p className="text-lg font-bold accent-green">...</p>
              </div>
              <p className="text-xs text-gray-500">Loading...</p>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Logged in view
  if (user) {
    return (
      <nav className="bg-black border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition" onClick={onHomeClick}>
              <div className="w-8 h-8 bg-green-btn rounded-full flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 4l12 8-12 8z" fill="#0c0c0c" />
                </svg>
              </div>
              <span className="text-xl md:text-2xl font-bold tracking-tighter">DevVital</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Live</p>
                <p className="text-lg font-bold accent-green">{liveCount}</p>
              </div>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-10 h-10 bg-green-btn rounded-full flex items-center justify-center font-bold text-black text-sm hover:opacity-90 transition"
                >
                  {getInitials(profile?.name, user.email)}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-md bg-[#1e1e1e] border border-white/10 rounded-lg shadow-xl z-50">
                    <div className="p-4 border-b border-white/10">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500">Logged in as</p>
                      <p className="font-bold text-white mt-1">{profile?.name || 'Anonymous'}</p>
                      <p className="text-xs text-gray-400 break-all mt-1">{user.email}</p>
                      <p className="text-[11px] text-gray-500 mt-2">User ID: {user.id.slice(0, 12)}...</p>
                      <p className="text-[11px] text-gray-500">Joined: {new Date(user.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="p-3 border-b border-white/10 space-y-2">
                      <p className="text-[11px] uppercase tracking-widest text-gray-500">Your referral link</p>
                      <div className="bg-[#111] border border-[#222] rounded px-2 py-2 text-xs text-white flex items-center justify-between gap-2">
                        <span className="truncate">{referralCode ? `${siteUrl}/?ref=${referralCode}` : 'Generating...'}</span>
                        <button
                          onClick={handleShare}
                          disabled={!referralCode}
                          className="text-[#50fa7b] hover:opacity-80 disabled:opacity-40 text-[11px] font-semibold"
                        >
                          Share
                        </button>
                      </div>
                      {shareMessage && <p className="text-[11px] text-gray-400">{shareMessage}</p>}
                    </div>
                    <div className="p-2 space-y-1">
                      {profile?.is_admin && (
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            onAdminClick();
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-[#50fa7b] hover:bg-white/5 rounded transition font-semibold"
                        >
                          Admin Panel
                        </button>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/5 rounded transition"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Not logged in view
  return (
    <nav className="bg-black border-b border-white/10">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition" onClick={onHomeClick}>
            <div className="w-8 h-8 bg-green-btn rounded-full flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 4l12 8-12 8z" fill="#0c0c0c" />
              </svg>
            </div>
            <span className="text-xl md:text-2xl font-bold tracking-tighter">DevVital</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-widest text-gray-500">Live</p>
              <p className="text-lg font-bold accent-green">{liveCount}</p>
            </div>
            <button
              onClick={onToggleRightSidebar}
              className="px-4 py-2 bg-green-btn text-black text-sm font-bold rounded hover:opacity-90 transition"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;